"use client";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Label,
  makeStyles,
  MessageBar,
  Spinner,
  Switch,
  tokens,
} from "@fluentui/react-components";
import { ArrowLeftFilled, Link20Regular } from "@fluentui/react-icons";
import { useMsal } from "@skanska/vsa-client-auth";
import { getOpenLink } from "@skanska/vsa-file-icon";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { chapterImageSize } from "../../constants/ImageConstants";
import {
  useGetDraft,
  useGetDraftAndPublishedFileNames,
  useUpdateDraft,
} from "../../hooks/useImages";
import { useGetUserAccessLevel } from "../../hooks/useGetUserAccessLevel";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import { ClientRoutes } from "../../models/ClientRoutes";
import { Image } from "../../models/Image";
import {
  clearErrorsByType,
  getMediaFileTypeDisplay,
  handleFileNameChangeUnified,
  handleFileSelection,
  ImageUploadType,
  isImageFile,
  resolveImageFileNameWithExtension,
  splitFileName,
  validateImageFormUnified,
} from "../../services/ImageService";
import { missingImagePlaceholder } from "../../services/MissingImagePlaceholder";
import { SkanskaColorTokens } from "../../style/colors";
import { CheckoutMessageBar } from "../CheckoutMessageBar";
import { SaveSplitButton } from "../SaveSplitButton";
import { StickyTopBannerStack } from "../StickyTopBannerStack";
import { CancelImageDraftButton } from "./CancelImageDraftButton";
import { ImagePublishDialog } from "./ImagePublishDialog";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingVerticalM,
    paddingRight: tokens.spacingVerticalM,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingBottom: tokens.spacingVerticalL,
  },
  title: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    margin: 0,
    textAlign: "center",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    maxWidth: "800px",
    margin: "0 auto",
    padding: tokens.spacingVerticalM,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  fileSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    alignItems: "center",
    paddingTop: tokens.spacingVerticalM,
  },
  statusContainer: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXL,
  },
  statusControl: {
    display: "flex",
    alignItems: "center",
  },
  actionButtons: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    justifyContent: "center",
  },
  deleteSection: {
    display: "flex",
    justifyContent: "center",
    paddingTop: tokens.spacingVerticalS,
  },
  buttons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingTop: tokens.spacingVerticalM,
  },
  notFound: {
    textAlign: "center",
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
  imagePreview: {
    maxWidth: "300px",
    maxHeight: "200px",
    objectFit: "contain",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
  },
  imageContainer: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    alignItems: "flex-start",
  },
  errorContainer: {
    minHeight: "60px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    maxWidth: "800px",
    margin: "0 auto",
    paddingBottom: tokens.spacingVerticalL,
  },
  errorMessage: {
    wordWrap: "break-word",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    hyphens: "auto",
  },
  selectedFile: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
  fileNameInputContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "4px",
  },
  fileExtension: {
    color: "#888",
    backgroundColor: "#f3f3f3",
    border: "1px solid #ddd",
    borderRadius: "4px",
    padding: "0 8px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    userSelect: "none",
    pointerEvents: "none",
  },
  deletefileButton: {
    backgroundColor: SkanskaColorTokens.color_tertiary_red_30,
    ":hover, :focus, :active": {
      backgroundColor: SkanskaColorTokens.color_tertiary_red,
      color: SkanskaColorTokens.color_secondary_white,
    },
  },
  filePickerInput: {
    display: "none",
  },
  filePickerButton: {
    width: "fit-content",
  },
  imageLink: {
    textDecoration: "none",
    color: "inherit",
    display: "flex",
    alignItems: "center",
    alignContent: "center",
    gap: "4px",
    marginTop: "8px",
  },
  errorList: {
    margin: "10px",
    paddingLeft: "0",
    listStyle: "none",
  },
  fileErrorMessage: {
    marginTop: "0",
  },
  fileErrorMessageWithErrors: {
    marginTop: tokens.spacingVerticalS,
  },
  imagePreviewHidden: {
    display: "none",
  },
  cancelFileButton: {
    marginTop: tokens.spacingVerticalXS,
  },
});

export const ActivityImageEditor = ({ id }: { id: string }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const filePickerRef = useRef<HTMLInputElement>(null);
  const fileNameBeforeFileSelection = useRef("");
  const { instance } = useMsal();
  const currentUser = instance.getActiveAccount();

  const [title, setTitle] = useState("");
  const [fileName, setFileName] = useState("");
  const [newFile, setNewFile] = useState<File | null>(null);
  const [currentImage, setCurrentImage] = useState<Image | null>(null);
  const [hasFormAction, setHasFormAction] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(true);
  const [isActive, setIsActive] = useState(true);
  const [isArchived, setIsArchived] = useState(false);
  const [archiveBlockedDialogOpen, setArchiveBlockedDialogOpen] =
    useState(false);
  const [publishOpenSignal, setPublishOpenSignal] = useState(0);

  const { data: activityImage, isLoading: activityImageLoading } =
    useGetDraft(id);
  const { data: publishedActivityNames, isLoading: activityNamesLoading } =
    useGetDraftAndPublishedFileNames();
  const { mutateAsync: updateDraftImage, isPending: isUpdatingDraft } =
    useUpdateDraft(id);

  const currentImageData = activityImage;
  const isLoading = activityImageLoading || activityNamesLoading;
  const publishedNames = publishedActivityNames;
  const accessLevel = useGetUserAccessLevel();

  const isCurrentUsersDraft = useMemo<boolean>(() => {
    if (!currentImage) {
      return true;
    }

    const userEntraId = currentUser?.idTokenClaims?.oid;

    if (!userEntraId) {
      return false;
    }

    return userEntraId === currentImage.createdBy?.id;
  }, [currentImage, currentUser]);

  // Set image data when loaded
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    if (currentImageData) {
      setCurrentImage(currentImageData);
      setTitle(currentImageData.title);
      setFileName(
        resolveImageFileNameWithExtension(currentImageData) ||
          currentImageData.title
      );
      setHasFormAction(false);

      const activeValue = currentImageData.isActive ?? true;
      const archivedValue = currentImageData.isArchived ?? false;
      setIsActive(activeValue);
      setIsArchived(archivedValue);

      // If image is base64, set loading to false immediately
      if (currentImageData.webUrl?.startsWith("data:")) {
        setImageLoading(false);
      } else {
        setImageLoading(true);
      }
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [currentImageData]);

  useEffect(() => {
    if (publishedNames && currentImage) {
      const validateForm = async () => {
        const formData = {
          title: title.trim(),
          name: fileName.trim(),
          file: newFile,
        };
        const validation = await validateImageFormUnified(
          formData,
          "activity",
          publishedNames,
          chapterImageSize.width,
          chapterImageSize.height,
          t,
          resolveImageFileNameWithExtension(currentImage) || currentImage.title
        );
        setErrors(validation.errors);
      };
      validateForm();
    }
  }, [title, fileName, publishedNames, currentImage, newFile, t]);

  const isUsedInPublishedActivities =
    (currentImage?.activities?.length ?? 0) > 0;
  const isArchivingUsedImage =
    isUsedInPublishedActivities && !currentImage?.isArchived && isArchived;

  const clearUnsavedChanges = useUnsavedChanges(
    hasFormAction,
    t("unsavedChangesMessage")
  );

  const handleFileSelect = async (selectedFile: File) => {
    if (!isCurrentUsersDraft) {
      return;
    }

    setHasFormAction(true);

    const result = await handleFileSelection(
      selectedFile,
      "activity",
      chapterImageSize.width,
      chapterImageSize.height,
      errors,
      t
    );

    setErrors(result.updatedErrors);

    if (result.isValid) {
      fileNameBeforeFileSelection.current = fileName;
      setNewFile(selectedFile);
      setFileError(null);

      // Extract the new extension from the selected file
      const selectedFileExt = selectedFile.name.includes(".")
        ? selectedFile.name.substring(selectedFile.name.lastIndexOf("."))
        : "";

      // Keep the current name but replace with the new extension
      const currentName = splitFileName(fileName).name;
      const newFileName = currentName + selectedFileExt;

      setFileName(newFileName);

      // Validate the new filename
      const fileNameResult = handleFileNameChangeUnified(
        newFileName,
        result.updatedErrors,
        publishedNames,
        "activity",
        t,
        resolveImageFileNameWithExtension(currentImage) || currentImage?.title
      );
      setErrors(fileNameResult.updatedErrors);
    } else {
      setNewFile(null);
      setFileError(result.fileError || null);
    }
  };

  const handleCancelFileSelection = () => {
    if (!isCurrentUsersDraft) {
      return;
    }

    setHasFormAction(true);
    setNewFile(null);
    setFileName(fileNameBeforeFileSelection.current);
    fileNameBeforeFileSelection.current = "";
    setFileError(null);
    setErrors((prev) => clearErrorsByType(prev, "file", t));
  };

  const handleSave = async () => {
    if (!currentImage || !id || !isCurrentUsersDraft) return;

    if (isArchivingUsedImage) {
      setArchiveBlockedDialogOpen(true);
      return;
    }

    setErrors([]);
    const formData = {
      title: title.trim(),
      name: fileName.trim(),
      file: newFile,
    };
    const validation = await validateImageFormUnified(
      formData,
      "activity",
      publishedNames,
      chapterImageSize.width,
      chapterImageSize.height,
      t,
      resolveImageFileNameWithExtension(currentImage) || currentImage.title
    );

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append(
        "VersionId",
        currentImage.versionId || currentImage.id
      );
      formDataToSend.append("Title", title.trim());
      formDataToSend.append("FileName", fileName.trim());
      formDataToSend.append("IsActive", String(isActive));
      formDataToSend.append("IsArchived", String(isArchived));

      if (newFile) {
        formDataToSend.append("NewImageFile", newFile);
      }

      const updatedImage = await updateDraftImage(formDataToSend);

      // Update currentImage state with the response
      if (updatedImage) {
        setCurrentImage(updatedImage);
        setFileName(
          resolveImageFileNameWithExtension(updatedImage) || updatedImage.title
        );
        const updatedActive = updatedImage.isActive ?? true;
        const updatedArchived = updatedImage.isArchived ?? false;
        setIsActive(updatedActive);
        setIsArchived(updatedArchived);
      }

      setNewFile(null);
      setHasFormAction(false);
    } catch (error) {
      console.error("Failed to save image:", error);
      setErrors([t("saveImageError") || "Failed to save image"]);
    }
  };

  const handleSaveAndPublish = (): void => {
    if (isArchivingUsedImage) {
      setArchiveBlockedDialogOpen(true);
      return;
    }

    setPublishOpenSignal((signal) => signal + 1);
  };

  const pendingImageUpdate = useMemo(
    () => ({
      title: title.trim(),
      fileName: fileName.trim(),
      isActive,
      isArchived,
      newFile,
    }),
    [title, fileName, isActive, isArchived, newFile]
  );

  if (isLoading) {
    return <Spinner size="large" />;
  }

  if (!currentImage) {
    return (
      <div className={classes.root}>
        <div className={classes.notFound}>{t("imageNotFound")}</div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {!isCurrentUsersDraft && currentImage.createdBy && (
        <StickyTopBannerStack>
          <CheckoutMessageBar checkedOutTo={currentImage.createdBy} />
        </StickyTopBannerStack>
      )}
      <div className={classes.header}>
        <h1 className={classes.title}>{t("editActivityImage")}</h1>
      </div>

      <div className={classes.errorContainer}>
        {errors.length > 0 && (
          <MessageBar intent="error">
            <ul className={classes.errorList}>
              {errors.map((error, index) => (
                <li key={index} className={classes.errorMessage}>
                  {error}
                </li>
              ))}
            </ul>
          </MessageBar>
        )}
        {fileError && (
          <MessageBar
            intent="error"
            className={
              errors.length > 0
                ? classes.fileErrorMessageWithErrors
                : classes.fileErrorMessage
            }
          >
            <div className={classes.errorMessage}>{fileError}</div>
          </MessageBar>
        )}
      </div>

      <div className={classes.form}>
        <div className={classes.field}>
          <Label>{t("currentFile")}</Label>
          <div className={classes.imageContainer}>
            {currentImage &&
              currentImage.webUrl &&
              (isImageFile(currentImage.webUrl, currentImage.fileName) ? (
                <div>
                  {imageLoading && <Spinner size="medium" />}
                  <img
                    src={currentImage.webUrl}
                    alt={currentImage.title}
                    width={240}
                    height={180}
                    title={currentImage.title}
                    className={`${classes.imagePreview} ${imageLoading ? classes.imagePreviewHidden : ""}`}
                    onClick={() => window.open(currentImage.webUrl, "_blank")}
                    onLoad={() => setImageLoading(false)}
                    onError={(e) => {
                      setImageLoading(false);
                      if (e.currentTarget.src !== missingImagePlaceholder) {
                        e.currentTarget.src = missingImagePlaceholder;
                      }
                    }}
                  />
                </div>
              ) : (
                <>
                  <a
                    className={classes.imageLink}
                    href={getOpenLink({
                      webUrl: currentImage.webUrl,
                      fileType:
                        getMediaFileTypeDisplay(currentImage) ||
                        currentImage.fileType,
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Link20Regular />
                    {currentImage.webUrl}
                  </a>
                </>
              ))}
          </div>
        </div>

        <div className={classes.fileSection}>
          <Field label={t("changeImage")}>
            <>
              <Button
                appearance="secondary"
                className={classes.filePickerButton}
                disabled={!isCurrentUsersDraft || isUpdatingDraft}
                onClick={() => filePickerRef.current?.click()}
              >
                {t("chooseFile")}
              </Button>
              <input
                ref={filePickerRef}
                type="file"
                accept={ImageUploadType.Activity}
                disabled={!isCurrentUsersDraft || isUpdatingDraft}
                onChange={async (e) => {
                  const selectedFile = e.target.files?.[0];
                  if (selectedFile) {
                    await handleFileSelect(selectedFile);
                  } else {
                    handleCancelFileSelection();
                  }
                }}
                className={classes.filePickerInput}
              />
            </>
          </Field>
          {newFile && (
            <div className={classes.selectedFile}>
              <Label>
                {t("selectedFile")}: <strong>{newFile.name}</strong>
              </Label>
              <div className={classes.cancelFileButton}>
                <Button
                  appearance="secondary"
                  onClick={handleCancelFileSelection}
                  disabled={!isCurrentUsersDraft || isUpdatingDraft}
                  size="medium"
                  className={classes.deletefileButton}
                >
                  {t("cancelSelectedNewImageFile")}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className={classes.field}>
          <Field label={t("title")} required hint={t("imageTitleHint")}>
            <Input
              value={title}
              disabled={!isCurrentUsersDraft || isUpdatingDraft}
              onChange={(_e, data) => {
                setHasFormAction(true);
                setTitle(data.value);

                // Clear existing title errors first
                let updatedErrors = clearErrorsByType(errors, "title", t);

                // Add title required error if empty
                if (!data.value.trim()) {
                  updatedErrors = [...updatedErrors, t("titleRequired")];
                }

                setErrors(updatedErrors);
              }}
              placeholder={t("enterImageTitle")}
            />
          </Field>
        </div>

        <div className={classes.field}>
          <Field label={t("fileName")} required hint={t("fileNameHint")}>
            <div className={classes.fileNameInputContainer}>
              <Input
                value={splitFileName(fileName).name}
                disabled={!isCurrentUsersDraft || isUpdatingDraft}
                onChange={(_e, data) => {
                  setHasFormAction(true);
                  // Combine the edited name with the existing extension
                  const { ext } = splitFileName(fileName);
                  const newFileName = data.value + ext;
                  setFileName(newFileName);

                  const result = handleFileNameChangeUnified(
                    newFileName,
                    errors,
                    publishedNames,
                    "activity",
                    t,
                    resolveImageFileNameWithExtension(currentImage) ||
                      currentImage?.title
                  );

                  setErrors(result.updatedErrors);
                }}
                placeholder={t("enterFileName")}
                style={{ flex: 1 }}
              />
              <span
                className={classes.fileExtension}
                aria-disabled="true"
                tabIndex={-1}
              >
                {splitFileName(fileName).ext}
              </span>
            </div>
          </Field>
        </div>

        <div className={classes.statusContainer}>
          <div className={classes.statusControl}>
            <Switch
              checked={isActive}
              label={t("imageActive")}
              disabled={!isCurrentUsersDraft || isArchived || isUpdatingDraft}
              onChange={(_event, data) => {
                setHasFormAction(true);
                setIsActive(data.checked);
              }}
            />
          </div>
          <div className={classes.statusControl}>
            <Checkbox
              checked={isArchived}
              label={t("archived")}
              disabled={!isCurrentUsersDraft || isUpdatingDraft}
              onChange={(_event, data) => {
                setHasFormAction(true);
                const checked = data.checked === true;
                setIsArchived(checked);
                if (checked) {
                  setIsActive(false);
                }
              }}
            />
          </div>
        </div>

        <div className={classes.actions}>
          <div className={classes.actionButtons}>
            {isCurrentUsersDraft && (
              <SaveSplitButton
                size="large"
                saveText={isUpdatingDraft ? t("saving") : t("save")}
                onSave={handleSave}
                saveDisabled={
                  !hasFormAction ||
                  !isCurrentUsersDraft ||
                  errors.length > 0 ||
                  fileError !== null ||
                  isUpdatingDraft
                }
                isSaveLoading={isUpdatingDraft}
                showSaveAndPublishOption={
                  hasFormAction &&
                  isCurrentUsersDraft &&
                  errors.length === 0 &&
                  fileError === null &&
                  !isUpdatingDraft
                }
                saveAndPublishText={t("saveAndPublish")}
                onSaveAndPublish={handleSaveAndPublish}
                saveAndPublishDisabled={
                  !isCurrentUsersDraft ||
                  errors.length > 0 ||
                  fileError !== null ||
                  isUpdatingDraft
                }
              />
            )}
            {currentImage && currentImage.versionId && (
              <ImagePublishDialog
                versionId={currentImage.versionId}
                imageId={currentImage.imageId || currentImage.versionId}
                disabled={hasFormAction || isUpdatingDraft}
                pendingUpdate={pendingImageUpdate}
                openSignal={publishOpenSignal}
                onPublishSuccess={clearUnsavedChanges}
                publishOnly={!isCurrentUsersDraft}
              />
            )}
            {!isCurrentUsersDraft &&
              accessLevel >= 3 &&
              currentImage.versionId && (
                <CancelImageDraftButton
                  versionId={currentImage.versionId}
                  imageId={currentImage.imageId}
                  requiresSecondConfirmation={!isCurrentUsersDraft}
                  checkedOutToName={
                    currentImage.createdBy?.displayName ||
                    currentImage.createdBy?.email ||
                    t("unknownUser")
                  }
                />
              )}
          </div>

          {isCurrentUsersDraft && (
            <div className={classes.deleteSection}>
              {accessLevel >= 3 && currentImage.versionId && (
                <CancelImageDraftButton
                  versionId={currentImage.versionId}
                  imageId={currentImage.imageId}
                  requiresSecondConfirmation={!isCurrentUsersDraft}
                  checkedOutToName={
                    currentImage.createdBy?.displayName ||
                    currentImage.createdBy?.email ||
                    t("unknownUser")
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>

      <div className={classes.buttons}>
        <Button
          icon={<ArrowLeftFilled />}
          size="small"
          onClick={() => navigate(ClientRoutes.MediaActivityPage)}
        >
          {t("back")}
        </Button>
      </div>
      <Dialog
        open={archiveBlockedDialogOpen}
        onOpenChange={(_, data) => setArchiveBlockedDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {t("imageUsedInPublishedActivitiesTitle")}
            </DialogTitle>
            <DialogContent>
              {t("imageUsedInPublishedActivitiesMessage")}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setArchiveBlockedDialogOpen(false)}>
                {t("close")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
